import { Box, Button, Flex, useDisclosure } from '@chakra-ui/react'
import { useApp, useAuth, usePracticeAccess, useScreen } from '@hb/react'
import { getPracticeAssessmentCollectionPath, PracticeAssessmentData } from '@hb/shared'
import { useEffect, useMemo } from 'react'
import { useDocument } from '../../../backend'
import { ColorTagPicker } from '../../../components/DataList/columns/assessments'
import { useProfileLayout } from '../../../contexts/user/ProfileLayoutContext'
import { ExamListGroupProvider } from '../../Encounters/context'
import { EncounterForm } from '../../Encounters/EncounterForm'
import { EncounterListGroupHeader } from '../../Encounters/EncounterListGroupHeader'
import { EncounterView } from '../../Encounters/EncounterView'
import { LaborChartingProvider, useLaborCharting } from './context'
import { LaborSectionPreview } from './LaborSectionPreview'
import { LogContactModal } from './LogContactModal'
import { PatientFlags } from './PatientFlags'
import { PostpartumSection } from './PostpartumSection'
import { PrenatalSection } from './PrenatalSection'

// §0.5.8 Color dot tag in chart header — subscribes to practice assessment snippet for current tag.
const ChartColorTag = ({
  patientId,
  assessmentId,
}: {
  patientId: string
  assessmentId: string
}) => {
  const { authUser } = useAuth()
  const { selectedPracticeId } = usePracticeAccess()
  const { appName } = useApp()
  const collectionPath = useMemo(
    () => (selectedPracticeId ? getPracticeAssessmentCollectionPath(selectedPracticeId) : null),
    [selectedPracticeId],
  )
  const { data: snippet } = useDocument<PracticeAssessmentData>(collectionPath, assessmentId)
  const currentTag = authUser?.uid ? snippet?.colorTagsByProvider?.[authUser.uid] : undefined

  if (!selectedPracticeId || !currentTag) return null
  return (
    <ColorTagPicker
      assessmentId={assessmentId}
      patientId={patientId}
      practiceId={selectedPracticeId}
      appName={appName}
      currentTag={currentTag}
    />
  )
}

// ── Main adaptive charting content ──────────────────────────

const ChartingContent = ({
  patientId,
  assessmentId,
}: {
  patientId: string
  assessmentId: string
}) => {
  const { width } = useScreen()
  const mobileView = useMemo(() => width < 1200, [width])
  const { openExam, setOpenExam, encounterOpenState } = useLaborCharting()
  // §1.6 / M.2: status-driven routing between the read-only view and the form.
  const { surface, onEdit, onSaveSuccess, amendmentReason, onAmendmentReasonConsumed } =
    encounterOpenState
  const { isOpen: isContactOpen, onOpen: openContact, onClose: closeContact } = useDisclosure()

  const encounterFormOpen = surface === 'form'
  const { setFocusMode, layoutMode } = useProfileLayout()
  const mobileLayout = layoutMode === 'mobile'
  useEffect(() => {
    setFocusMode(encounterFormOpen)
    return () => setFocusMode(false)
  }, [encounterFormOpen, setFocusMode])

  if (openExam) {
    return surface === 'view' && openExam.examId ? (
      <EncounterView
        patientId={patientId}
        examId={openExam.examId}
        onClose={() => setOpenExam(null)}
        onEdit={onEdit}
      />
    ) : (
      <Box w="100%" h="100%" overflowY="auto" bg="#efefef" _dark={{ bg: 'gray.900' }}>
        <EncounterForm
          patientId={patientId}
          openEncounter={openExam}
          assessmentId={assessmentId}
          amendmentReason={amendmentReason}
          onAmendmentReasonConsumed={onAmendmentReasonConsumed}
          onClose={() => setOpenExam(null)}
          onSuccess={onSaveSuccess}
        />
      </Box>
    )
  }

  return (
    <Flex
      direction="column"
      w="100%"
      p={4}
      overflowY="auto"
      bg="gray.100"
      // bg="#efefef"
      _dark={{ bg: 'gray.700' }}
      h="100%">
      <Flex align="center" gap={1} flexWrap="wrap" rowGap={1}>
        <EncounterListGroupHeader
          title="Pregnancy Charting"
          flexProps={{
            bg: 'transparent',
            boxShadow: 'none',
            px: 2,
            pb: 0,
            pt: 0,
            flex: 1,
            _dark: { bg: 'transparent', boxShadow: 'none' },
          }}
        />
        {/* §0.5.8 Color dot tag — provider assigns tag per patient, visible in chart header */}
        <ChartColorTag patientId={patientId} assessmentId={assessmentId} />
        {/* §2.17: + Log Contact — always visible regardless of pregnancy stage */}
        <Button
          size={mobileLayout ? 'xs' : 'sm'}
          variant="outline"
          bg="white"
          colorScheme="teal"
          fontFamily="Hero-New"
          fontWeight={600}
          flexShrink={0}
          onClick={openContact}>
          + Log Contact
        </Button>
      </Flex>
      {/* §0.5.6 Provider and system flags — displayed below header on every charting screen */}
      <PatientFlags assessmentId={assessmentId} patientId={patientId} />
      <Flex direction="column" gap={4}>
        <PrenatalSection assessmentId={assessmentId} />
        <LaborSectionPreview mobileView={mobileView} />
        <PostpartumSection />
      </Flex>

      <LogContactModal
        assessmentId={assessmentId}
        patientId={patientId}
        isOpen={isContactOpen}
        onClose={closeContact}
      />
    </Flex>
  )
}

// ── Exported component ──────────────────────────────────────

export const LaborCharting = ({
  patientId,
  assessmentId,
}: {
  patientId: string
  assessmentId: string
}) => (
  <LaborChartingProvider>
    <ExamListGroupProvider patientId={patientId}>
      <ChartingContent patientId={patientId} assessmentId={assessmentId} />
    </ExamListGroupProvider>
  </LaborChartingProvider>
)
